import React, { useEffect, useMemo, useState } from "react";
import { Integration } from "../../../../../../../redux/slices/projects/Types";
import StatusTag, { IntegrationStatusLabel } from "../shared/StatusTag";
import {
  Button,
  DatePicker,
  Dropdown,
  dropDownThemes,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  Modal,
  TextArea,
} from "@hid-galaxy-ui/galaxy-react";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux";
import {
  getTimelineEntries,
  mapTimelineEntryToStepEntry,
  getCategoryId,
  formatDateForDB,
  formatDateForDisplay,
} from "../shared/timeline-helpers";
import { createTimelineEntrySagaAction, updateTimelineEntrySagaAction } from "../../../../../../../redux/saga/projects/saga-actions";
import { CreateTimelineEntryRequest } from "../../../../../../../redux/slices/projects/Types";
import "./ValidationStep.scss";
import { useTimelineStepPermissions } from "../../../../../../../hooks/useTimelineStepPermissions";
import { useResolveSubmittedBy } from "../shared/useResolveSubmittedBy";

export type ValidationEntry = {
  id?: number;
  category?: string;
  start_date: string | null;
  completion_date: string | null;
  status: IntegrationStatusLabel;
  notes?: string | null;
  submitted_by?: number | null;
  submission_date?: string | null;
  created_at?: string;
};

interface ValidationStepProps {
  integration?: Integration;
  isFormDisabled?: boolean;
}

export function getValidationEntries(
  integration?: Integration
): ValidationEntry[] {
  const timelineEntries = getTimelineEntries(
    integration?.timeline,
    "Validation"
  );
  if (timelineEntries.length > 0) {
    return timelineEntries.map((entry) =>
      mapTimelineEntryToStepEntry<ValidationEntry>(entry)
    );
  }
  return [];
}

export function getValidationCurrentStatus(
  integration?: Integration
): IntegrationStatusLabel | null {
  // Return null if timeline is empty or doesn't exist
  if (
    !integration?.timeline ||
    Object.keys(integration.timeline).length === 0
  ) {
    return null;
  }

  const timelineEntries = getTimelineEntries(
    integration.timeline,
    "Validation"
  );
  if (timelineEntries.length === 0) {
    return null;
  }

  const entries = timelineEntries.map((entry) =>
    mapTimelineEntryToStepEntry<ValidationEntry>(entry)
  );
  return entries?.[0]?.status || null;
}

const ValidationStep: React.FC<ValidationStepProps> = ({ integration, isFormDisabled = false }) => {
  const dispatch = useAppDispatch();
  const { projectDetails, status: projectStatus } = useAppSelector((state) => state.projects);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const {
    canAccessTimeline,
    canAccessNotesHistory,
    timelineLocked,
    resolveCommentsModalMode,
  } = useTimelineStepPermissions(isFormDisabled);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isRowEditing, setIsRowEditing] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [commentsModalMode, setCommentsModalMode] = useState<
    "view" | "add" | "edit"
  >("view");
  const [commentsModalNote, setCommentsModalNote] = useState<string>("");
  const [notesDraft, setNotesDraft] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [completionDate, setCompletionDate] = useState<Date | null>(null);
  const [status, setStatus] = useState<IntegrationStatusLabel | null>(null);

  const entries = useMemo(
    () => getValidationEntries(integration),
    [integration]
  );

  const [editingCommentIndex, setEditingCommentIndex] = useState<number | null>(
    null
  );
  const [commentDraft, setCommentDraft] = useState<string>("");

  const statusOptions = useMemo(
    () => [
      { label: "Completed", value: "Completed" as IntegrationStatusLabel },
      { label: "In progress", value: "In progress" as IntegrationStatusLabel },
      { label: "On hold", value: "On hold" as IntegrationStatusLabel },
      {
        label: "Not applicable",
        value: "Not applicable" as IntegrationStatusLabel,
      },
    ],
    []
  );

  const parseDateOrNull = (dateStr: string | null): Date | null => {
    if (!dateStr) return null;
    const m = moment(dateStr, ["DD/MM/YYYY", "D/M/YYYY", "DD MMM, YYYY"], true);
    if (m.isValid()) return m.toDate();
    const fallback = new Date(dateStr);
    return isNaN(fallback.getTime()) ? null : fallback;
  };

  useEffect(() => {
    const latest = entries?.[0] || null;
    setStartDate(parseDateOrNull(latest?.start_date || null));
    setCompletionDate(parseDateOrNull(latest?.completion_date || null));
    setStatus(latest?.status || null);
    setEditingCommentIndex(null);
    setCommentDraft("");
  }, [entries]);

  const resolveSubmittedByName = useResolveSubmittedBy(
    entries.map((e) => e.submitted_by)
  );

  const latestEntry = entries?.[0] ?? null;
  const canSave =
    !!status &&
    (status === "Not applicable" || !!startDate) &&
    !timelineLocked &&
    !projectStatus.timelineUpdateLoading;

  const openCommentsModal = (
    mode: "view" | "add" | "edit",
    note?: string,
    editEntryIndex?: number
  ) => {
    const effectiveMode = resolveCommentsModalMode(mode);
    const noteText =
      note ??
      (editEntryIndex !== undefined
        ? ((entries[editEntryIndex]?.notes || "") as string)
        : "");
    setCommentsModalMode(effectiveMode);
    setCommentsModalNote(noteText);
    if (effectiveMode === "edit" && editEntryIndex !== undefined) {
      setEditingCommentIndex(editEntryIndex);
      setCommentDraft(noteText.slice(0, 500));
    }
    setIsCommentsModalOpen(true);
  };

  const closeCommentsModal = () => {
    setIsCommentsModalOpen(false);
    setCommentsModalNote("");
    if (commentsModalMode === "edit") {
      setEditingCommentIndex(null);
      setCommentDraft("");
    }
  };

  const onRowEditStart = () => {
    setStartDate(parseDateOrNull(latestEntry?.start_date ?? null));
    setCompletionDate(parseDateOrNull(latestEntry?.completion_date ?? null));
    setStatus(latestEntry?.status ?? null);
    setNotesDraft((latestEntry?.notes as string) ?? "");
    setIsRowEditing(true);
  };

  const onRowEditCancel = () => {
    setStartDate(parseDateOrNull(latestEntry?.start_date ?? null));
    setCompletionDate(parseDateOrNull(latestEntry?.completion_date ?? null));
    setStatus(latestEntry?.status ?? null);
    setNotesDraft((latestEntry?.notes as string) ?? "");
    setIsRowEditing(false);
  };

  const onRowEditConfirm = () => {
    onUpdateClick();
    setIsRowEditing(false);
  };

  const onUpdateClick = () => {
    const isNotApplicable = status === "Not applicable";
    if (!integration?.integration_id || !status || (!isNotApplicable && !startDate)) {
      // Validate required fields
      return;
    }

    const submittedBy = loggedUserDetail?.ID || 0;

    // Dates are optional for "Not applicable". If user provides dates, persist them.
    // If user leaves start empty, persist a "zero date" so UI renders "-" via formatDateForDisplay.
    const startDateForPayload = startDate ? formatDateForDB(startDate) : "0000-00-00";
    const completionDateForPayload = completionDate
      ? formatDateForDB(completionDate)
      : "";

    const categoryId = getCategoryId(integration?.timeline, "Validation");
    if (!categoryId) {
      return;
    }

    const payload: CreateTimelineEntryRequest = {
      category: categoryId,
      parent_category_id: null,
      submitted_by: submittedBy,
      start_date: startDateForPayload,
      completion_date: completionDateForPayload,
      status: status,
      note: (notesDraft || "").slice(0, 500),
    };

    dispatch(
      createTimelineEntrySagaAction(integration.integration_id, payload)
    );
  };

  const onCancelCommentEdit = () => {
    setEditingCommentIndex(null);
    setCommentDraft("");
  };

  const onSaveCommentEdit = () => {
    if (timelineLocked || editingCommentIndex === null) return;

    const entry = entries[editingCommentIndex];
    if (!entry?.id) {
      onCancelCommentEdit();
      return;
    }

    const payload = {
      note: (commentDraft || "").slice(0, 500),
    };

    dispatch(updateTimelineEntrySagaAction(entry.id, payload));
    setCommentDraft("");
  };

  return (
    <div className="validation-step">
      {canAccessTimeline ? (
        <div className="validation-edit">
          <div className="validation-table-card onboarding-table-card">
            <div className="onboarding-table">
              <div className="onboarding-table__row onboarding-table__header">
                <div className="onboarding-table__cell submitted-by">Submitted by</div>
                <div className="onboarding-table__cell status">Status</div>
                <div className="onboarding-table__cell start">Start</div>
                <div className="onboarding-table__cell completion">Completion</div>
                <div className="onboarding-table__cell comments">Comments</div>
                <div className="onboarding-table__cell action">Action</div>
              </div>
              <div className="onboarding-table__row onboarding-table__body-row">
                <div className="onboarding-table__cell submitted-by">
                  {isRowEditing
                    ? resolveSubmittedByName(latestEntry?.submitted_by)
                    : resolveSubmittedByName(latestEntry?.submitted_by) || "-"}
                </div>
                <div className="onboarding-table__cell status">
                  {isRowEditing ? (
                    <div className="onboarding-table__input-wrap">
                      <Dropdown
                        id="validation_status"
                        name="validation_status"
                        label=""
                        options={statusOptions}
                        selected={statusOptions.find((opt) => opt.value === status)}
                        onSelect={(opt) => {
                          const nextStatus =
                            (opt?.value as IntegrationStatusLabel) || status;
                          setStatus(nextStatus);
                        }}
                        placeholder="Select"
                        menuPortalTarget={document.body}
                        isClearable={false}
                        theme={dropDownThemes.LIGHT}
                        disabled={timelineLocked}
                      />
                    </div>
                  ) : latestEntry?.status ? (
                    <StatusTag status={latestEntry.status} />
                  ) : (
                    "-"
                  )}
                </div>
                <div className="onboarding-table__cell start">
                  {isRowEditing ? (
                    <div className="onboarding-table__input-wrap">
                      <DatePicker
                        id="validation_start_date"
                        name="validation_start_date"
                        label=""
                        format="dd/MM/yyyy"
                        placeholder="dd/mm/yy"
                        value={startDate}
                        onChange={(date) =>
                          setStartDate(Array.isArray(date) ? date[0] : date)
                        }
                        isRequired={status !== "Not applicable"}
                        disabled={timelineLocked}
                      />
                    </div>
                  ) : (
                    formatDateForDisplay(latestEntry?.start_date ?? null) || "-"
                  )}
                </div>
                <div className="onboarding-table__cell completion">
                  {isRowEditing ? (
                    <div className="onboarding-table__input-wrap">
                      <DatePicker
                        id="validation_completion_date"
                        name="validation_completion_date"
                        label=""
                        format="dd/MM/yyyy"
                        placeholder="dd/mm/yy"
                        value={completionDate}
                        onChange={(date) =>
                          setCompletionDate(Array.isArray(date) ? date[0] : date)
                        }
                        disabled={timelineLocked}
                      />
                    </div>
                  ) : (
                    formatDateForDisplay(latestEntry?.completion_date ?? null) || "-"
                  )}
                </div>
                <div className="onboarding-table__cell comments">
                  <button
                    type="button"
                    className="comments-link"
                    onClick={() => {
                      if (isRowEditing) {
                        openCommentsModal("add");
                      } else {
                        openCommentsModal("view", (latestEntry?.notes as string) ?? "");
                      }
                    }}
                  >
                    {isRowEditing ? "Add" : latestEntry?.status === "Completed" ? "View" : "Add"}
                  </button>
                </div>
                <div className="onboarding-table__cell action">
                  {isRowEditing ? (
                    <div className="onboarding-table__action-btns">
                      <button
                        type="button"
                        className="action-btn"
                        onClick={onRowEditConfirm}
                        disabled={!canSave}
                        aria-label="Save"
                      >
                        {projectStatus.timelineUpdateLoading ? (
                          <Loader variant={LoaderSizes.small} />
                        ) : (
                          <Iconography icon="check" size={IconographySizes.Small} />
                        )}
                      </button>
                      <button
                        type="button"
                        className="action-btn"
                        onClick={onRowEditCancel}
                        disabled={projectStatus.timelineUpdateLoading}
                        aria-label="Cancel"
                      >
                        <Iconography icon="xmark" size={IconographySizes.Small} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="action-btn"
                      onClick={onRowEditStart}
                      disabled={timelineLocked}
                      aria-label="Edit"
                    >
                      <Iconography icon="pen" size={IconographySizes.Small} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {isCommentsModalOpen && (
            <Modal
              headerText={
                commentsModalMode === "view"
                  ? "Comments"
                  : commentsModalMode === "edit"
                    ? "Edit notes"
                    : "Add notes"
              }
              width={500}
              onClose={closeCommentsModal}
              className="comments-modal"
            >
              <div className="modal-content">
                {commentsModalMode === "view" ? (
                  <TextArea
                    id="validation_notes_view"
                    name="validation_notes_view"
                    label="Notes"
                    placeholder=""
                    rows={4}
                    value={commentsModalNote || "-"}
                    maxLength={500}
                    disabled
                    onChange={(_e: React.ChangeEvent<HTMLTextAreaElement>) => {}}
                  />
                ) : commentsModalMode === "edit" ? (
                  <TextArea
                    id="validation_notes_edit"
                    name="validation_notes_edit"
                    label="Notes"
                    placeholder="Add notes here..."
                    rows={4}
                    value={commentDraft}
                    maxLength={500}
                    disabled={timelineLocked}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setCommentDraft(e.currentTarget.value.slice(0, 500))
                    }
                  />
                ) : (
                  <TextArea
                    id="validation_notes_modal"
                    name="validation_notes_modal"
                    label="Notes"
                    placeholder="Add notes here..."
                    rows={4}
                    value={notesDraft}
                    maxLength={500}
                    disabled={timelineLocked}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setNotesDraft(e.currentTarget.value.slice(0, 500))
                    }
                  />
                )}
                <div className="modal-footer">
                  <Button
                    variant="secondary"
                    label={commentsModalMode === "view" ? "CLOSE" : "CANCEL"}
                    normalCase={false}
                    onClick={closeCommentsModal}
                  />
                  {(commentsModalMode === "add" || commentsModalMode === "edit") &&
                    !timelineLocked && (
                    <Button
                      variant="primary"
                      label="SAVE"
                      normalCase={false}
                      onClick={() => {
                        if (commentsModalMode === "edit") onSaveCommentEdit();
                        closeCommentsModal();
                      }}
                      disabled={
                        commentsModalMode === "edit" &&
                        projectStatus.timelineUpdateLoading
                      }
                    />
                  )}
                </div>
              </div>
            </Modal>
          )}

          {canAccessNotesHistory && (
          <>
          <div className="validation-edit__footer">
            <button
              type="button"
              className="history-toggle"
              onClick={() => setIsHistoryOpen((v) => !v)}
            >
              <span>
                {isHistoryOpen ? "Notes history" : "Notes history"}
              </span>
              <Iconography
                icon={isHistoryOpen ? "xmark" : "chevronDown"}
                size={IconographySizes.Small}
              />
            </button>
          </div>

          {isHistoryOpen && (
            <div className="validation-history-card onboarding-table-card">
              <div className="onboarding-table">
                <div className="onboarding-table__row onboarding-table__header">
                  <div className="onboarding-table__cell submitted-by">Category</div>
                  <div className="onboarding-table__cell submitted-by">Submitted by</div>
                  <div className="onboarding-table__cell status">Status</div>
                  <div className="onboarding-table__cell start">Start</div>
                  <div className="onboarding-table__cell completion">Completion</div>
                  <div className="onboarding-table__cell comments">Comments</div>
                </div>

                {entries.length === 0 ? (
                  <div className="onboarding-table__row onboarding-table__body-row onboarding-table__empty">
                    <div className="onboarding-table__cell" style={{ width: "100%", textAlign: "center", padding: "24px" }}>
                      No history available
                    </div>
                  </div>
                ) : (
                  entries.map((entry, idx) => (
                    <div className="onboarding-table__row onboarding-table__body-row" key={idx}>
                      <div className="onboarding-table__cell submitted-by">
                        {entry.category || "-"}
                      </div>
                      <div className="onboarding-table__cell submitted-by">
                        {resolveSubmittedByName(entry.submitted_by)}
                      </div>
                      <div className="onboarding-table__cell status">
                        <StatusTag status={entry.status || "Not applicable"} />
                      </div>
                      <div className="onboarding-table__cell start">
                        {formatDateForDisplay(entry.start_date ?? null) || "-"}
                      </div>
                      <div className="onboarding-table__cell completion">
                        {formatDateForDisplay(entry.completion_date ?? null) || "-"}
                      </div>
                      <div className="onboarding-table__cell comments">
                        <button
                          type="button"
                          className="comments-link"
                          onClick={() =>
                            openCommentsModal(
                              timelineLocked ? "view" : "edit",
                              (entry.notes as string) ?? "",
                              idx
                            )
                          }
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
          </>
          )}
        </div>
      ) : (
        <div className="validation-partner-table-card">
          <div className="validation-grid">
            <div className="grid-row grid-header">
              {/* <div className="grid-cell grid-cell--process">Process</div> */}
              <div className="grid-cell">Submitted by</div>
              <div className="grid-cell grid-cell--status">Status</div>
              <div className="grid-cell">Start Date</div>
              <div className="grid-cell">Completion Date</div>
            </div>

            {entries.map((entry) => (
              <div className="grid-row" key={entry.id}>
                {/* <div className="grid-cell grid-cell--process">Validation</div> */}
                <div className="grid-cell">{resolveSubmittedByName(entry.submitted_by)}</div>
                <div className="grid-cell grid-cell--status">
                  <StatusTag status={entry.status || "Not applicable"} />
                </div>
                <div className="grid-cell">
                  {formatDateForDisplay(entry.start_date || null)}
                </div>
                <div className="grid-cell">
                  {formatDateForDisplay(entry.completion_date || null)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationStep;
